const AG_GRID_SCROLL_SELECTORS =
  '.ag-root, .ag-center-cols-viewport, .ag-body-viewport';

/**
 * Shows a global action popup menu anchored to a DOM element, with outside click and scroll handling.
 */
export function showGlobalActionPopup(
  anchorEl: HTMLElement,
  actions: {
    key: string;
    label: string;
    isPriv: boolean;
    disabled?: boolean;
  }[],
  onAction: (_actionKey: string, _event?: MouseEvent) => void,
  options?: {
    leftOffset?: number;
    topOffset?: number;
    popupHeight?: number;
  },
  disabledTitle?: string,
): () => void {
  const existing = document.getElementById('ag-global-action-popup');
  if (existing) existing.remove();

  const rect = anchorEl.getBoundingClientRect();
  const popup = createActionPopupUtil(rect, actions, options, disabledTitle);
  document.body.appendChild(popup);

  const handlePopupClick = (e: MouseEvent) => {
    const t = e.target as HTMLElement;
    const action = t.getAttribute('data-action');
    if (action) {
      onAction(action, e);
      cleanup();
    }
  };
  popup.addEventListener('mousedown', handlePopupClick);
  const handleOutsideClick = (e: MouseEvent) => {
    if (!popup.contains(e.target as Node) && e.target !== anchorEl) {
      cleanup();
    }
  };
  const handleScroll = () => cleanup();
  setTimeout(() => {
    document.addEventListener('mousedown', handleOutsideClick);
    window.addEventListener('scroll', handleScroll, { once: true });
    document.querySelectorAll(AG_GRID_SCROLL_SELECTORS).forEach((container) => {
      container.addEventListener('scroll', handleScroll, { once: true });
    });
  }, 0);

  function cleanup() {
    popup.remove();
    document.removeEventListener('mousedown', handleOutsideClick);
    window.removeEventListener('scroll', handleScroll);
    document.querySelectorAll(AG_GRID_SCROLL_SELECTORS).forEach((container) => {
      container.removeEventListener('scroll', handleScroll);
    });
  }
  return cleanup;
}

/**
 * Creates a global action popup for ag-Grid action columns.
 */
export function createActionPopupUtil(
  rect: DOMRect,
  actions: {
    key: string;
    label: string;
    isPriv: boolean;
    disabled?: boolean;
  }[],
  options?: {
    leftOffset?: number;
    topOffset?: number;
    popupHeight?: number;
  },
  disabledTitle?: string,
): HTMLDivElement {
  const { leftOffset = 160, popupHeight = 180, topOffset = 0 } = options || {};
  const popup = document.createElement('div');
  popup.id = 'ag-global-action-popup';
  popup.className = 'ag-action-popup';
  let top = rect.bottom + topOffset;
  const viewportHeight =
    window.innerHeight || document.documentElement.clientHeight;
  if (top + popupHeight > viewportHeight && rect.top > popupHeight) {
    top = rect.top - rect.height - topOffset;
  }
  popup.style.top = `${top}px`;
  popup.style.left = `${rect.right - leftOffset}px`;
  popup.style.maxHeight = popupHeight + 'px';
  popup.innerHTML = actions
    .map((ele) => {
      if (!ele.isPriv) return '';
      const disabled = ele.disabled ? 'true' : 'false';
      const disabledClass = ele.disabled ? 'table-actions-disabled' : '';
      const title = ele.disabled ? (disabledTitle ?? '') : '';
      return `<div class="ag-action-item ${disabledClass}" data-action="${ele.key}" data-disabled="${disabled}" title="${title}">
          <span class="material-symbols-outlined table-actions ${disabledClass}" id="${ele.key}" data-action="${ele.key}" data-disabled="${disabled}">${ele.key}</span>
          <span data-action="${ele.key}" class="table-actions ${disabledClass}" data-disabled="${disabled}">${ele.label}</span>
        </div>`;
    })
    .join('');
  return popup;
}
import { CellClickedEvent, ICellRendererParams } from 'ag-grid-enterprise';

export const tableActionCellRenderer = (
  params?: CellClickedEvent | ICellRendererParams,
  titles?: {
    save?: string;
    cancel?: string;
    edit?: string;
    history?: string;
    view?: string;
    archive?: string;
    restore?: string;
    delete?: string;
  },
  isHistory?: boolean,
  openDialog?: boolean,
  hasActions?: boolean,
  noActionText?: string,
) => {
  if (params?.data?.isEditable) {
    return `<span title="${titles?.save}" class="material-symbols-outlined table-actions table-actions-save" id="save">check</span> 
          &nbsp;
          <span title="${titles?.cancel}" class="material-symbols-outlined table-actions table-actions-close" id="cancel">close</span>`;
  } else if (isHistory) {
    const id = params?.data?.referenceId ?? params?.data?.attributeId ?? '';
    const name =
      params?.data?.referenceName ?? params?.data?.attributeName ?? '';
    return `
    <div class="triple-dot-container" title="${hasActions === false ? noActionText : ''}">
      <button class="ag-action-menu-btn triple-dot-menu-btn" 
        data-row-index="${params?.node.rowIndex}"
        data-id="${id}"
        data-name="${name}"
        ${hasActions === false ? 'disabled' : ''}
        >&#x22EE;</button>
    </div>
  `;
  } else if (openDialog) {
    return `
  <span title="${titles?.edit}" class="material-symbols-outlined table-actions" id="edit">edit</span>
  &nbsp;<span title="${titles?.delete}" class="material-symbols-outlined table-actions delete" id="delete">delete</span>
  `;
  } else {
    return `<span title="${titles?.edit}" class="material-symbols-outlined table-actions" id="edit">edit</span>`;
  }
};

export const gridDateFormatter = {
  comparator: (filterLocalDateAtMidnight: Date, cellValue: string) => {
    if (!cellValue) return -1;
    const cellDate = new Date(cellValue);
    const filterDateOnly = new Date(
      filterLocalDateAtMidnight.getFullYear(),
      filterLocalDateAtMidnight.getMonth(),
      filterLocalDateAtMidnight.getDate(),
      0,
      0,
      0,
      0,
    );
    const cellDateOnly = new Date(
      cellDate.getFullYear(),
      cellDate.getMonth(),
      cellDate.getDate(),
      0,
      0,
      0,
      0,
    );
    if (cellDateOnly < filterDateOnly) return -1;
    if (cellDateOnly > filterDateOnly) return 1;
    return 0;
  },
  browserDatePicker: true,
  inRangeInclusive: true,
};
